import {flatten} from 'lodash';
import LRU from 'lru-cache';
import {hslToColorString} from 'polished';
import {HslaColor} from 'polished/lib/types/color';

import {isHtmlElement} from '../../../helpers/src/browser/domHelpers';
import {collectEnumValues} from '../../../helpers/src/typescript/enumHelpers';
import {parseToHsla} from './colorHelpers';

interface ComputedStyles {
  backgroundColor: string;
  color: string;
  borderBottomColor: string;
  borderBottomStyle: string;
  borderBottomWidth: string;
  borderLeftColor: string;
  borderLeftStyle: string;
  borderLeftWidth: string;
  borderRightColor: string;
  borderRightStyle: string;
  borderRightWidth: string;
  borderTopColor: string;
  borderTopStyle: string;
  borderTopWidth: string;
}

enum BorderDirectionsEnum {
  TOP = 'top',
  LEFT = 'left',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

const borderStyleNames = {
  [BorderDirectionsEnum.TOP]: {
    widthCssName: 'borderTopWidth',
    colorCssName: 'borderTopColor',
    styleCssName: 'borderTopStyle',
  },
  [BorderDirectionsEnum.BOTTOM]: {
    widthCssName: 'borderBottomWidth',
    colorCssName: 'borderBottomColor',
    styleCssName: 'borderBottomStyle',
  },
  [BorderDirectionsEnum.LEFT]: {
    widthCssName: 'borderLeftWidth',
    colorCssName: 'borderLeftColor',
    styleCssName: 'borderLeftStyle',
  },
  [BorderDirectionsEnum.RIGHT]: {
    widthCssName: 'borderRightWidth',
    colorCssName: 'borderRightColor',
    styleCssName: 'borderRightStyle',
  },
} as const;

export enum CssPropertiesEnum {
  BACKGROUND_COLOR = 'background-color',
  COLOR = 'color',
  BORDER_BOTTOM_COLOR = 'border-bottom-color',
  BORDER_TOP_COLOR = 'border-top-color',
  BORDER_RIGHT_COLOR = 'border-right-color',
  BORDER_LEFT_COLOR = 'border-left-color',
}

const cssProperties = collectEnumValues(CssPropertiesEnum);

const borderDirectionToCssPropertyMap = {
  [BorderDirectionsEnum.TOP]: CssPropertiesEnum.BORDER_TOP_COLOR,
  [BorderDirectionsEnum.BOTTOM]: CssPropertiesEnum.BORDER_BOTTOM_COLOR,
  [BorderDirectionsEnum.RIGHT]: CssPropertiesEnum.BORDER_RIGHT_COLOR,
  [BorderDirectionsEnum.LEFT]: CssPropertiesEnum.BORDER_LEFT_COLOR,
} as const;

// Normally, a signficant amount of time goes into parsing color strings.
// Since emails typically use a small number of colors, memoization
// provides unusually high value. This essentially eliminates the time
// spent processing colors.
const makeParseToHslaMemoized = () => {
  const cache = new LRU<string, Readonly<HslaColor>>({
    max: 1000,
  });

  return (color: string) => {
    const hslaColor = cache.get(color) || parseToHsla(color);
    cache.set(color, hslaColor);
    return hslaColor;
  };
};

export const parseToHslaMemoized = makeParseToHslaMemoized();

export const computeInvertedBackgroundColor = (hslColor: Readonly<HslaColor>, appForegroundColor: string) => {
  // If the color is a light grey, clamp it to our background color.
  if (appForegroundColor && hslColor.lightness > 0.6 && hslColor.saturation < 0.3) {
    return appForegroundColor;
  }

  // Highlights (background with high transparency) should be color inverted.
  if (hslColor.alpha !== undefined && hslColor.alpha < 0.5) {
    return hslToColorString({
      ...hslColor,
      lightness: 1 - hslColor.lightness,
    });
  }

  // Otherwise, dark backgrounds with colors should be set to a low lightness.
  return hslToColorString({
    ...hslColor,
    lightness: 0.1,
  });
};

// Color refers to the literal "color" CSS property, not any color.
export const computeInvertedColor = (hslColor: Readonly<HslaColor>) =>
  hslToColorString({
    ...hslColor,
    // Colors tend to look a bit desaturated when converted from dark to light.
    saturation: Math.min(1, hslColor.saturation * 1.2),
    // Colors which look fine on light can lack contrast when inverted on dark backgrounds.
    // Pure white also doesn't look good, so we clamp the lightness between two values.
    lightness: Math.max(0.7, Math.min(0.98, 1 - hslColor.lightness)),
  });

export const computeInvertedBorderColor = (hslColor: Readonly<HslaColor>) =>
  hslToColorString({
    ...hslColor,
    // Inverted borders tend to be too bright, so we limit the lightness.
    lightness: Math.max(0.3, Math.min(0.6, 1 - hslColor.lightness)),
  });

type ColorFinder = (hslColor: Readonly<HslaColor>, appForegroundColor: string) => string;

const colorFinder: Record<CssPropertiesEnum, ColorFinder> = {
  [CssPropertiesEnum.BACKGROUND_COLOR]: computeInvertedBackgroundColor,
  [CssPropertiesEnum.COLOR]: computeInvertedColor,
  [CssPropertiesEnum.BORDER_BOTTOM_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_LEFT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_RIGHT_COLOR]: computeInvertedBorderColor,
  [CssPropertiesEnum.BORDER_TOP_COLOR]: computeInvertedBorderColor,
};

const buildDataAttribute = (property: CssPropertiesEnum) => `data-${property}`;

const invertNodeColor = (
  node: HTMLElement,
  property: CssPropertiesEnum,
  color: string,
  appForegroundColor: string,
) => {
  const hslColor = parseToHslaMemoized(color);

  // The browser returns rgb(0, 0, 0, 0) if computed styles are not set. Skip the transparent case.
  if (hslColor.alpha === 0) {
    return;
  }

  // Note: The browser setProperty API uses hypenated CSS names (e.g. border-color not borderColor.)
  node.style.setProperty(property, colorFinder[property](hslColor, appForegroundColor), 'important');
  // Set the original color as a data attribute so it can be restored.
  node.setAttribute(buildDataAttribute(property), color);
};

// Restores colors inverted by this module.
export const restoreOriginalColors = (node: HTMLElement) => {
  for (const property of cssProperties) {
    const dataAttr = buildDataAttribute(property);
    if (node.hasAttribute(dataAttr)) {
      node.style.setProperty(property, node.getAttribute(dataAttr));
      node.removeAttribute(dataAttr);
    }
  }
};

// getComputedStyle is forced to perform style recalculation if we alternate
// reads and writes. Since the getComputedStyle object is live (reads are done
// just in time,) we need to store the values instead of using the getComputedStyle
// object directly.
const findComputedStylesForNode = (node: HTMLElement): ComputedStyles => {
  const styles = getComputedStyle(node);
  return {
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    borderBottomColor: styles.borderBottomColor,
    borderBottomStyle: styles.borderBottomStyle,
    borderBottomWidth: styles.borderBottomWidth,
    borderLeftColor: styles.borderLeftColor,
    borderLeftStyle: styles.borderLeftStyle,
    borderLeftWidth: styles.borderLeftWidth,
    borderRightColor: styles.borderRightColor,
    borderRightStyle: styles.borderRightStyle,
    borderRightWidth: styles.borderRightWidth,
    borderTopColor: styles.borderTopColor,
    borderTopStyle: styles.borderTopStyle,
    borderTopWidth: styles.borderTopWidth,
  };
};

// Whether the border is rendered by the browser.
const isBorderVisible = (width: string, style: string) => width !== '0px' && style !== 'none';

interface NodeInfo {
  node: HTMLElement;
  styles: ComputedStyles;
  hasTextNodeChild: boolean;
}

const invertNodeColors = ({node, styles, hasTextNodeChild}: NodeInfo, appForegroundColor: string) => {
  invertNodeColor(node, CssPropertiesEnum.BACKGROUND_COLOR, styles.backgroundColor, appForegroundColor);

  // If an element doesn't have any direct descendants which are
  // text nodes, skip color inversion. List items are also inverted so
  // bullets and numbers are correctly colored.
  if (hasTextNodeChild || node.tagName === 'LI') {
    invertNodeColor(node, CssPropertiesEnum.COLOR, styles.color, appForegroundColor);
  }

  // Iterate over top, left, right, and bottom border values.
  for (const direction of collectEnumValues(BorderDirectionsEnum)) {
    // This looks up the CSS names (e.g. borderBottomWidth)
    const {widthCssName, styleCssName, colorCssName} = borderStyleNames[direction];

    // These are the computed style values.
    const borderWidth = styles[widthCssName];
    const borderStyle = styles[styleCssName];
    const borderColor = styles[colorCssName];
    if (isBorderVisible(borderWidth, borderStyle)) {
      // Need to map camel case names to hyphenated CSS names.
      invertNodeColor(node, borderDirectionToCssPropertyMap[direction], borderColor, appForegroundColor);
    }
  }
};

// For nodes which already have a dark background, we skip traversing into them.
// This has some edge cases (e.g. if there's a light background nested within a
// dark background) but that's an uncommon enough case where this generally has
// good results, and in that case it just remains the way it looked originally.
const shouldSkipSubtree = (backgroundColor: string) => {
  const {lightness, alpha} = parseToHslaMemoized(backgroundColor);
  return lightness < 0.6 && alpha === 1;
};

// This is needed for skipped subtrees, which should retain their original colors,
// and not inherit dark mode flipped colors set on parent elements.
const setOriginalColorOnNode = (node: HTMLElement, styles: ComputedStyles) => {
  node.style.setProperty(CssPropertiesEnum.COLOR, styles.color);
};

// Computed styles are collected before any nodes are processed to
// avoid expensive style recalculation.
const recursivelyCollectNodeColors = (node: HTMLElement) => {
  const styles = findComputedStylesForNode(node);
  const results: NodeInfo[][] = [];

  if (shouldSkipSubtree(styles.backgroundColor)) {
    setOriginalColorOnNode(node, styles);
    return [];
  }

  let hasTextNodeChild = false;
  for (const child of Array.from(node.childNodes)) {
    if (isHtmlElement(child)) {
      results.push(recursivelyCollectNodeColors(child));
    } else if (child.nodeType === Node.TEXT_NODE) {
      hasTextNodeChild = true;
    }
  }

  results.push([
    {
      styles,
      node,
      hasTextNodeChild,
    },
  ]);

  return flatten(results);
};

// Primary export. Use this function to convert a DOM tree to dark mode.
// Returns true if the DOM tree was converted to dark mode, false if conversion was skipped
// due to the size of the tree.
export const convertToDarkMode = (root: HTMLElement, appForegroundColor: string) => {
  // Temporarily set color-scheme to light to ensure system colors are set correctly.
  // For example, the CSS2 color keyword "windowtext" is black when color-scheme is light,
  // but white when color-scheme is dark.
  const originalColorScheme = root.style.getPropertyValue('color-scheme');
  root.style.setProperty('color-scheme', 'light');

  for (const nodeInfo of recursivelyCollectNodeColors(root)) {
    invertNodeColors(nodeInfo, appForegroundColor);
  }

  root.style.setProperty('color-scheme', originalColorScheme);
};
